/* eslint-disable promise/always-return */
/* eslint-disable promise/catch-or-return */
import campaignService from 'tradera/apps/syi/services/campaign-service';
import ThrottledAction from 'tradera/apps/syi/utils/throttled-action';
import userData from 'tradera/apps/syi/services/user-data';
import { setDisplayPromoted, setDisplayHighlighted } from './draft-actions';
import { getSyiSlice } from 'tradera/apps/syi/state/selectors/syi';
import type { AppDispatch, GetState } from 'tradera/state/configure-store';
import {
    disableZeroDayAuctions,
    enableRestart,
    enableZeroDayAuctions
} from '../reducers/item-duration';
import { resetSelectedShippingOptions } from 'tradera/apps/syi/state/reducers/shipping/shipping-reducer';
import {
    initializeCampaignCodeState,
    resetCampaignBanner,
    resetFees,
    searchingForCampaignCodeIsDelayed,
    setCampaignValidationResult,
    setUnverifiedCampaignCode,
    toggleCampaignCodeCheckbox,
    validatingCampaignCodeBySearching
} from 'tradera/apps/syi/state/reducers/campaign-code';
import type { Charity } from 'tradera/state/services/types/webapi-selling-generated';
import { disableRestart } from 'tradera/apps/syi/state/reducers/draft';
import {
    resetCharityCampaign as resetCharityCampaignReducer,
    setCharityCampaign
} from 'tradera/apps/syi/state/reducers/charity-campaign';

const CHARITIES = userData?.charities || [];
const throttleTimeout = 2000;
const throttledCampaignCodeValidation = new ThrottledAction(throttleTimeout);
let delayedValidationTimeoutId: ReturnType<typeof setTimeout> | undefined =
    undefined;

const findBrandAttributeValues = (getState: GetState) => {
    if (
        !getSyiSlice(getState()).attributes ||
        !getSyiSlice(getState()).attributes.categoryAttributes
    ) {
        return [];
    }

    const brandAttribute = getSyiSlice(
        getState()
    ).attributes.categoryAttributes.find((attribute) => {
        const brandAttributeId = 3; //Brand Attribute has Id == 3
        if (attribute.id != brandAttributeId) {
            return false;
        }
        if (attribute.options.length === 0) {
            return false;
        }
        return true;
    });

    const brandAttributeValues = brandAttribute?.options
        .filter((option) => option.isActive)
        .map((option) => option.value);
    return brandAttributeValues ?? [];
};

const resetCharityCampaign = (dispatch: AppDispatch, getState: GetState) => {
    const { name, forceFreeShipping: wasForceFreeShipping } =
        getSyiSlice(getState()).charityCampaign;
    if (!name) {
        return;
    }
    dispatch(resetCharityCampaignReducer());
    dispatch(disableZeroDayAuctions());
    if (wasForceFreeShipping) {
        dispatch(resetSelectedShippingOptions());
    }
};

const resetCampaign = (dispatch: AppDispatch) => {
    dispatch(resetCharityCampaign);
    dispatch({
        type: 'UNSET_UNSOLD_FEE_FREE_FOR_CAMPAIGN'
    });
    dispatch(enableRestart());
    dispatch(resetFees());
    dispatch(resetCampaignBanner());
};

const applyCharityFromCampaignCodeId =
    (campaignCodeId: number, campaignCode: string) =>
    (dispatch: AppDispatch) => {
        const charityCampaign = CHARITIES.find(
            (charityCampaign: Charity) =>
                charityCampaign.campaignCodeId === campaignCodeId
        );

        if (!charityCampaign) {
            dispatch(resetCharityCampaign);
            return;
        }

        dispatch(setCharityCampaign({ charityCampaign, campaignCode }));
        const { allowZeroDayAuctions, forceFreeShipping } = charityCampaign;

        if (allowZeroDayAuctions) {
            dispatch(enableZeroDayAuctions());
        }

        if (forceFreeShipping) {
            dispatch({
                type: 'FORCE_FREE_SHIPPING'
            });
        }

        dispatch(setDisplayPromoted(false));
        dispatch(setDisplayHighlighted(false));
    };

const validateCampaignCode =
    () => (dispatch: AppDispatch, getState: GetState) => {
        const campaignCode = getSyiSlice(getState()).campaignCode.campaignCode;
        if (!campaignCode || campaignCode.trim().length === 0) {
            return;
        }
        dispatch(validatingCampaignCodeBySearching());
        const isReadOnly = getSyiSlice(getState()).campaignCode.isReadOnly;
        throttledCampaignCodeValidation.execute(function () {
            const category = getSyiSlice(getState()).category;
            const brandAttributeValues = findBrandAttributeValues(getState);
            campaignService
                .validate(
                    campaignCode,
                    category.draftCategoryId.toString(),
                    brandAttributeValues
                )
                .then((result) => {
                    const {
                        bannerDesktopImageUrl,
                        bannerMobileImageUrl,
                        bannerTabletImageUrl,
                        campaignCodeId,
                        hasEnded,
                        isUnsoldFeeFree,
                        maxFeeCap,
                        provisionFactor = null,
                        validationDisplayMessage
                    } = result;

                    // If the campaign is read-only, ignore if the code is invalid since it has already been used.
                    const campaignCodeValid = isReadOnly
                        ? true
                        : result.campaignCodeValid;
                    const campaignCodeMessage = isReadOnly
                        ? null
                        : result.campaignCodeMessage;

                    const campaignCodeDescription = campaignCodeValid
                        ? campaignCodeMessage
                        : validationDisplayMessage;

                    const commissionPercent =
                        provisionFactor !== null
                            ? `${provisionFactor * 100}`
                            : null;

                    dispatch(
                        setCampaignValidationResult({
                            bannerDesktopImageUrl,
                            bannerMobileImageUrl,
                            bannerTabletImageUrl,
                            campaignCode,
                            campaignCodeDescription,
                            campaignCodeId,
                            campaignCodeValid,
                            hasEnded,
                            maxFeeCap,
                            commissionPercent
                        })
                    );

                    if (!campaignCodeValid) {
                        dispatch(resetCampaign);
                        return;
                    }

                    if (isUnsoldFeeFree) {
                        dispatch({
                            type: 'SET_UNSOLD_FEE_FREE_FOR_CAMPAIGN'
                        });
                    }

                    dispatch(disableRestart());

                    if (campaignCodeId) {
                        dispatch(
                            applyCharityFromCampaignCodeId(
                                campaignCodeId,
                                campaignCode
                            )
                        );
                    }
                });
        });
    };

const saveTypedCampaignCode =
    (inputCampaignCode: string) =>
    (dispatch: AppDispatch, getState: GetState) => {
        const campaignCode = inputCampaignCode?.trim() || null;
        dispatch(setUnverifiedCampaignCode(campaignCode));

        if (delayedValidationTimeoutId) {
            clearTimeout(delayedValidationTimeoutId);
        }

        if (campaignCode === null) {
            dispatch(resetCampaign);
            return;
        }

        /* eslint-disable-next-line better-mutation/no-mutation */
        delayedValidationTimeoutId = setTimeout(() => {
            if (delayedValidationTimeoutId) {
                clearTimeout(delayedValidationTimeoutId);
            }
            dispatch(validateCampaignCode());
            /* eslint-disable-next-line better-mutation/no-mutation */
            delayedValidationTimeoutId = setTimeout(() => {
                if (getSyiSlice(getState()).campaignCode.searching) {
                    dispatch(searchingForCampaignCodeIsDelayed());
                }
            }, 100);
        }, 700);
    };

const setInitialCampaignCode =
    (
        campaignCode: string | null,
        campaignCodeId?: number,
        campaignCodeDescription?: string,
        isChangeItem?: boolean
    ) =>
    (dispatch: AppDispatch) => {
        const isReadOnly = !!campaignCodeId && !!isChangeItem;

        dispatch(
            initializeCampaignCodeState({
                campaignCode,
                campaignCodeId,
                campaignCodeDescription,
                isReadOnly
            })
        );
        dispatch(resetCampaign);

        // Always validate because this fetches info about the campaign like free shipping etc.
        // If the campaign is read-only, ignore if the code is invalid since it has already been used.
        dispatch(validateCampaignCode());
    };

const toggleShowInput = (isChecked: boolean) => (dispatch: AppDispatch) => {
    if (delayedValidationTimeoutId) {
        clearTimeout(delayedValidationTimeoutId);
    }
    dispatch(toggleCampaignCodeCheckbox(isChecked));
    dispatch(resetCampaign);
};

export default {
    toggleShowInput,
    setInitialCampaignCode,
    saveTypedCampaignCode,
    validateCampaignCode
};
